// API utility functions for backend communication
// Reference: HTTP Client implementation for ISMS Manager backend API
// Following RESTful API design patterns for authentication and authorization

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:3001/api';

interface ApiError {
  error: string;
  details?: any;
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token;
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if token exists
    if (this.token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${this.token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      
      // Handle different response types
      const contentType = response.headers.get('content-type');
      const isJson = contentType?.includes('application/json');
      
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          this.setToken(null);
          window.location.href = '/login';
          throw new Error('Authentication required');
        }

        // Throw API error
        const error: ApiError = isJson ? data : { error: data };
        throw new Error(error.error || 'API request failed');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  // GET request
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  // POST request
  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // POST request with FormData (for file uploads)
  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header for FormData - browser will set it with boundary
    };

    // Add authorization header if token exists
    if (this.token) {
      config.headers = {
        Authorization: `Bearer ${this.token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      
      // Handle different response types
      const contentType = response.headers.get('content-type');
      const isJson = contentType?.includes('application/json');
      
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          this.setToken(null);
          window.location.href = '/login';
          throw new Error('Authentication required');
        }

        // Throw API error
        const error: ApiError = isJson ? data : { error: data };
        throw new Error(error.error || 'API request failed');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  // Authentication methods
  async register(data: {
    email: string;
    firstName: string;
    lastName: string;
    role?: string;
    mfaSecret?: string;
  }) {
    return this.post('/auth/register', data);
  }

  async login(data: {
    email: string;
    password: string;
    mfaToken?: string;
    mfaMethod?: 'totp' | 'duo';
  }) {
    const response = await this.post<{
      user: any;
      token?: string;
      requiresMFA?: boolean;
      requiresMFASetup?: boolean;
      message: string;
    }>('/auth/login', data);

    if (response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  // Duo Push MFA
  async startDuo(email: string, password: string) {
    // May return { txid } or immediate { token, user }
    const response = await this.post<{ txid?: string; token?: string; user?: any; message?: string }>('/auth/duo/start', { email, password });
    if ((response as any).token) {
      this.setToken((response as any).token as string);
    }
    return response;
  }

  async checkDuo(email: string, password: string, txid: string) {
    const response = await this.post<{
      message?: string;
      user?: any;
      token?: string;
      status?: any;
    }>('/auth/duo/status', { email, password, txid });
    if (response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } finally {
      this.setToken(null);
    }
  }

  async getProfile() {
    return this.get<{ user: any }>('/auth/profile');
  }

  async updateProfile(data: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }) {
    return this.put('/auth/profile', data);
  }

  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }) {
    return this.post('/auth/change-password', data);
  }

  async requestPasswordReset(email: string) {
    return this.post('/auth/forgot-password', { email });
  }

  async resetPassword(data: {
    token: string;
    password: string;
  }) {
    return this.post('/auth/reset-password', data);
  }

  async verifyEmail(token: string) {
    return this.get(`/auth/verify-email/${token}`);
  }

  async checkAuth() {
    return this.get<{ authenticated: boolean; user: any }>('/auth/me');
  }

  // MFA methods
  async setupMFA() {
    return this.post<{
      secret: string;
      qrCode: string;
      message: string;
    }>('/auth/mfa/setup');
  }

  async setupMFAForRegistration(email: string) {
    return this.post<{
      secret: string;
      qrCode: string;
      message: string;
    }>('/auth/mfa/setup-registration', { email });
  }

  async verifyMFAForRegistration(secret: string, token: string) {
    return this.post<{
      success: boolean;
      message: string;
    }>('/auth/mfa/verify-registration', { secret, token });
  }

  async setupMFAPostLogin(email: string, password: string) {
    return this.post<{
      secret: string;
      qrCode: string;
      message: string;
    }>('/auth/mfa/setup-post-login', { email, password });
  }

  async completeMFAPostLogin(email: string, password: string, secret: string, token: string) {
    const response = await this.post<{
      message: string;
      user: any;
      token: string;
    }>('/auth/mfa/complete-post-login', { email, password, secret, token });

    if (response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  async enableMFA(token: string) {
    return this.post('/auth/mfa/enable', { token });
  }

  async disableMFA(password: string) {
    return this.post('/auth/mfa/disable', { password });
  }

  // Session management
  async getSessions() {
    return this.get<{ sessions: any[] }>('/auth/sessions');
  }

  async revokeSession(sessionId: string) {
    return this.delete(`/auth/sessions/${sessionId}`);
  }

  async revokeAllSessions() {
    return this.delete('/auth/sessions');
  }

  // Admin methods
  async getUsers(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.get<{
      users: any[];
      pagination: { limit: number; offset: number; total: number };
    }>(`/admin/users?${query.toString()}`);
  }

  async getUser(userId: string) {
    return this.get<{ user: any }>(`/admin/users/${userId}`);
  }

  async updateUser(userId: string, data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    isActive?: boolean;
  }) {
    return this.put(`/admin/users/${userId}`, data);
  }

  async deactivateUser(userId: string) {
    return this.post(`/admin/users/${userId}/deactivate`);
  }

  async activateUser(userId: string) {
    return this.post(`/admin/users/${userId}/activate`);
  }

  async resetUserPassword(userId: string) {
    return this.post(`/admin/users/${userId}/reset-password`);
  }

  async getAuditLogs(params?: {
    userId?: number;
    action?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.userId) query.append('userId', params.userId.toString());
    if (params?.action) query.append('action', params.action);
    if (params?.startDate) query.append('startDate', params.startDate);
    if (params?.endDate) query.append('endDate', params.endDate);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.get(`/admin/audit-logs?${query.toString()}`);
  }

  async getLoginStats(params?: {
    startDate?: string;
    endDate?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.startDate) query.append('startDate', params.startDate);
    if (params?.endDate) query.append('endDate', params.endDate);
    
    return this.get(`/admin/stats/logins?${query.toString()}`);
  }

  async getSecurityEvents(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.get(`/admin/security-events?${query.toString()}`);
  }

  async exportAuditLogs(params?: {
    startDate?: string;
    endDate?: string;
    format?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.startDate) query.append('startDate', params.startDate);
    if (params?.endDate) query.append('endDate', params.endDate);
    if (params?.format) query.append('format', params.format);
    
    return this.get(`/admin/audit-logs/export?${query.toString()}`);
  }

  async testEmailConfig() {
    return this.get('/admin/system/test-email');
  }

  async cleanupSessions() {
    return this.post('/admin/system/cleanup-sessions');
  }

  async cleanupAuditLogs(retentionDays?: number) {
    return this.post('/admin/system/cleanup-audit-logs', { retentionDays });
  }

  // New user management endpoints
  async getAllUsers(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.get(`/auth/admin/users?${query.toString()}`);
  }

  async getPendingUsers() {
    return this.get('/auth/admin/users/pending');
  }

  async approveUser(userId: number) {
    return this.post(`/auth/admin/users/${userId}/approve`);
  }

  async rejectUser(userId: number, data: { reason?: string }) {
    return this.post(`/auth/admin/users/${userId}/reject`, data);
  }

  async inviteUser(data: {
    email: string;
    firstName: string;
    lastName: string;
    role?: string;
  }) {
    return this.post('/auth/admin/users/invite', data);
  }

  async updateUserRole(userId: number, data: { role: string }) {
    return this.put(`/auth/admin/users/${userId}/role`, data);
  }

  async adminResetPassword(userId: number) {
    return this.post(`/auth/admin/users/${userId}/reset-password`);
  }

  async deleteUser(userId: number) {
    return this.delete(`/auth/admin/users/${userId}`);
  }

  async enableUserMFA(userId: number) {
    return this.post(`/auth/admin/users/${userId}/enable-mfa`);
  }

  async disableUserMFA(userId: number) {
    return this.post(`/auth/admin/users/${userId}/disable-mfa`);
  }

  async enableUserDuo(userId: number, duoUsername?: string) {
    return this.post(`/auth/admin/users/${userId}/enable-duo`, { duoUsername });
  }

  async disableUserDuo(userId: number) {
    return this.post(`/auth/admin/users/${userId}/disable-duo`);
  }

  async acceptInvitation(data: { token: string; password: string }) {
    return this.post('/auth/accept-invitation', data);
  }

  // User Preferences methods
  async getUserPreferences() {
    return this.get('/preferences');
  }

  async getSectionPreferences(section: string) {
    return this.get(`/preferences/${section}`);
  }

  async setSectionPreferences(section: string, preferences: Record<string, any>) {
    return this.put(`/preferences/${section}`, preferences);
  }

  async setSinglePreference(section: string, key: string, value: any) {
    return this.put(`/preferences/${section}/${key}`, { value });
  }

  async deleteSinglePreference(section: string, key: string) {
    return this.delete(`/preferences/${section}/${key}`);
  }

  async deleteSectionPreferences(section: string) {
    return this.delete(`/preferences/${section}`);
  }

  async deleteAllPreferences() {
    return this.delete('/preferences');
  }
}

// Export singleton instance
export const api = new ApiClient();
export default api;
